import { useCallback, useEffect, useRef, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { useCarFinanceLoan } from '../../queries/hooks/useCarFinanceLoan';
import { useTotalDepositContext } from '../../contexts/TotalDepositContext';

import { Text } from '../../../ToolkitV2/Text/Text';
import { LinkButton } from '../../../Toolkit/Button/LinkButton';
import { Link } from '../../../Toolkit/Button/Link';
import { SkeletonBlock } from '../../../ToolkitV2/Skeleton/Skeleton';
import { TextWithHelpIcon } from '../Panels/shared/TextWithHelpIcon/TextWithHelpIcon';
import { Spacer } from '../../../../helpers/genericStyles';
import { PriceField } from '../../financeProviders/shared/PriceField/PriceField';
import { Select } from '../../../Toolkit/Inputs/Select';
import { AdCard } from '../Panels/Introduction/components/AdCard/AdCard';
import { Divider } from '../../../ToolkitV2/Divider/Divider';

import * as Styled from './FinanceCalculator.styled';

import type { Form, LoanRequest } from '../../queries/schema/carFinance.schema';
import { FinanceCalculatorProps } from './FinanceCalculator.typed';
import {
  formatCurrencyDecimal,
  formatCurrencyInteger,
  formatPercent,
  DynamicCashDepositModal,
  DynamicHirePurchaseModal,
  DynamicTradeInValueModal,
  DynamicTotalDepositModal,
  DynamicOutstandingFinanceModal,
} from './helpers';
import { useFinanceCalculator } from './FinanceCalculator.hook';
import { pushToDataLayer } from '../../../../services/gtmService';

const BreakdownValue = ({
  isLoading,
  value,
  format,
  skeletonWidth = '64px',
  skeletonHeight = '14px',
}: {
  readonly isLoading: boolean;
  readonly value: number | null | undefined;
  readonly format: (v: number) => string;
  readonly skeletonWidth?: string;
  readonly skeletonHeight?: string;
}) => {
  if (isLoading)
    return <SkeletonBlock as="span" w={skeletonWidth} h={skeletonHeight} />;
  if (value == null) return <>{'—'}</>;
  return <>{format(value)}</>;
};

const FinanceCalculator = (props: FinanceCalculatorProps) => {
  const {
    dealerId,
    financeHouse,
    minimumDepositValue,
    minimumDepositPercentage,
    showOutstandingFinance = false,
    maxFinanceAmount,
    isCashDepositRequired = true,
    adId,
    onViewFullSummary,
    onFinanceTypeHelp,
    onChangePayload,
  } = props;

  const onChangePayloadRef = useRef(onChangePayload);
  onChangePayloadRef.current = onChangePayload;

  const hasTrackedInteraction = useRef(false);
  const trackCalculatorInteraction = useCallback(() => {
    if (!hasTrackedInteraction.current) {
      pushToDataLayer({ event: 'calculator_interaction' });
      hasTrackedInteraction.current = true;
    }
  }, []);

  const {
    price,
    control,
    getValues,
    onChangeHandlerFinanceInputs,
    tradeInValue,
    outstandingFinance,
    cashDeposit,
    onBlurFinanceInputs,
    isCashDepositModalOpen,
    setIsCashDepositModalOpen,
    isTradeInValueModalOpen,
    setIsTradeInValueModalOpen,
    isTotalDepositModalOpen,
    setIsTotalDepositModalOpen,
    isOutstandingFinanceModalOpen,
    setIsOutstandingFinanceModalOpen,
    isMonthlyRepaymentsDisabled,
    pricePerMonth: initialPricePerMonth,
    aprRate: initialAprRate,
  } = useFinanceCalculator({
    minimumDepositValue,
    minimumDepositPercentage,
    maxFinanceAmount,
  });

  // Future: remove this once the calculator owns its form via useForm<CalculatorFormValues>().
  // At that point setValue will come from the calculator's own useForm hook.
  const { setValue } = useFormContext<Form>();

  // Future: useWatch will be typed against CalculatorFormValues rather than the
  // full Form union once the calculator owns its form state.
  const [make, model, totalDeposit, financeAmount, monthlyInstallments] =
    useWatch({
      control,
      name: [
        'carDetails.make',
        'carDetails.model',
        'loanDetails.totalDeposit',
        'loanDetails.financeAmount',
        'loanDetails.monthlyInstallments',
      ],
    });

  // Future: replace with an internal debounce + validation when the calculator
  // no longer relies on TotalDepositProvider from the parent form.
  const { debouncedTotalDeposit, isValidDeposit } = useTotalDepositContext();

  const loanDetails = getValues('loanDetails');

  const loanOptions: LoanRequest = {
    ...loanDetails,
    monthlyInstallmentOptions: [],
    dealerId,
    financeHouse,
  };

  const { data, isLoading } = useCarFinanceLoan({
    options: loanOptions,
    enabled: isValidDeposit,
    dependencies: [debouncedTotalDeposit, monthlyInstallments],
  });

  useEffect(() => {
    const loanResult = isValidDeposit ? data : null;

    // Only overwrite the form's pricePerMonth once we have a live API result —
    // this preserves the pre-calculated default from initialFormData on page load.
    if (loanResult) {
      setValue('loanDetails.pricePerMonth', loanResult.pricePerMonth);
      setValue('loanDetails.aprRate', loanResult.apr);
    }

    // Future: this is the primary integration point for the IIFE host.
    // The host passes onChangePayload to receive updated loan values and
    // can use them to update external UI without any React or form context.
    onChangePayloadRef.current?.({
      monthlyPrice: loanResult?.pricePerMonth || undefined,
      financeType: 'Hire Purchase',
      totalDeposit,
      financeAmount,
      apr: loanResult?.apr,
      totalCostOfCredit: loanResult?.costOfCredit,
    });
  }, [data, isValidDeposit, setValue, totalDeposit, financeAmount]);

  const loanData = isValidDeposit && data ? data : null;

  const [isDisclaimerOpen, setIsDisclaimerOpen] = useState(false);
  const [isHirePurchaseModalOpen, setIsHirePurchaseModalOpen] = useState(false);

  const monthlyPrice = loanData?.pricePerMonth ?? initialPricePerMonth;
  const apr = loanData?.apr ?? initialAprRate;
  const totalCostOfCredit = loanData?.costOfCredit;

  return (
    <>
      <Styled.Grid>
        <div>
          {adId && price != null && (
            <AdCard
              adId={adId}
              title={`${make ?? ''} ${model ?? ''}`}
              price={price}
            />
          )}
          <Spacer marginBottom={'M24'} />
          <Styled.InputsContainer>
            <Controller
              name="loanDetails.cashDeposit"
              control={control}
              render={({ field, fieldState }) => (
                <PriceField
                  {...field}
                  {...fieldState}
                  onChange={(value: number | undefined) => {
                    trackCalculatorInteraction();
                    field.onChange(value);
                    onChangeHandlerFinanceInputs({
                      cashDeposit: value,
                      tradeInValue,
                      outstandingFinance,
                    });
                  }}
                  onBlur={onBlurFinanceInputs()}
                  label={
                    <TextWithHelpIcon
                      text={
                        isCashDepositRequired ? 'Cash deposit*' : 'Cash deposit'
                      }
                      onClickIcon={() => {
                        trackCalculatorInteraction();
                        setIsCashDepositModalOpen(true);
                      }}
                      aria-label="Cash deposit modal button"
                    />
                  }
                />
              )}
            />
            <Controller
              name="loanDetails.tradeInValue"
              control={control}
              render={({ field, fieldState }) => (
                <PriceField
                  {...field}
                  {...fieldState}
                  onChange={(value: number | undefined) => {
                    trackCalculatorInteraction();
                    field.onChange(value);
                    onChangeHandlerFinanceInputs({
                      cashDeposit,
                      tradeInValue: value,
                      outstandingFinance,
                    });
                  }}
                  onBlur={onBlurFinanceInputs()}
                  label={
                    <TextWithHelpIcon
                      text="Estimated trade-in value"
                      onClickIcon={() => {
                        trackCalculatorInteraction();
                        setIsTradeInValueModalOpen(true);
                      }}
                      aria-label="Trade-in value modal button"
                    />
                  }
                  topRightNode={
                    <Link
                      href="/trade-in-valuation"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-tracking="car-finance-get-trade-in-estimate-link"
                    >
                      Get estimate
                    </Link>
                  }
                />
              )}
            />
            {showOutstandingFinance && (
              <Controller
                name="loanDetails.outstandingLoan"
                control={control}
                render={({ field, fieldState }) => (
                  <PriceField
                    {...field}
                    {...fieldState}
                    onChange={(value: number | undefined) => {
                      trackCalculatorInteraction();
                      field.onChange(value);
                      onChangeHandlerFinanceInputs({
                        cashDeposit,
                        tradeInValue,
                        outstandingFinance: value,
                      });
                    }}
                    onBlur={onBlurFinanceInputs()}
                    label={
                      <TextWithHelpIcon
                        text="Outstanding finance"
                        onClickIcon={() => {
                          trackCalculatorInteraction();
                          setIsOutstandingFinanceModalOpen(true);
                        }}
                        aria-label="Outstanding finance modal button"
                      />
                    }
                  />
                )}
              />
            )}
          </Styled.InputsContainer>
        </div>
        <Styled.BreakdownPanel>
          <Styled.MonthlyPriceHeader>
            <div>
              <Text size="M16" color="GREY_DARK">
                Monthly price
              </Text>
              <Text size="H32" weight="bold" color="GREY_DARKER">
                <BreakdownValue
                  isLoading={isLoading}
                  value={monthlyPrice}
                  format={formatCurrencyDecimal}
                  skeletonWidth="100px"
                  skeletonHeight="28px"
                />
              </Text>
            </div>
            <Controller
              name="loanDetails.monthlyInstallments"
              control={control}
              render={({ field: { value, onChange, ...field } }) => (
                <Select
                  {...field}
                  value={value?.toString()}
                  onChange={(val) => {
                    trackCalculatorInteraction();
                    onChange(Number(val));
                  }}
                  options={
                    getValues('loanDetails.monthlyInstallmentOptions')?.map(
                      (opt) => ({
                        label: opt.displayValue,
                        value: opt.value.toString(),
                      }),
                    ) ?? []
                  }
                  label="Select a term*"
                  disabled={isMonthlyRepaymentsDisabled}
                  willUseSubText={false}
                />
              )}
            />
          </Styled.MonthlyPriceHeader>
          <Divider />
          <Spacer marginBottom={'M24'} />
          <Styled.BreakdownList>
            <Styled.BreakdownItem>
              <Text size="M16" color="GREY_DARK">
                Finance type
              </Text>
              <TextWithHelpIcon
                text="Hire purchase"
                onClickIcon={() => {
                  trackCalculatorInteraction();
                  setIsHirePurchaseModalOpen(true);
                  onFinanceTypeHelp?.();
                }}
                size="M16"
                color="GREY_DARKER"
                weight="bold"
                aria-label="Hire Purchase information"
              />
            </Styled.BreakdownItem>
            <Styled.BreakdownItem>
              <Text size="M16" color="GREY_DARK">
                Total deposit
              </Text>
              <Text size="M16" weight="bold" color="GREY_DARKER">
                <BreakdownValue
                  isLoading={isLoading}
                  value={totalDeposit}
                  format={formatCurrencyInteger}
                />
              </Text>
            </Styled.BreakdownItem>
            <Styled.BreakdownItem>
              <Text size="M16" color="GREY_DARK">
                Finance amount
              </Text>
              <Text size="M16" weight="bold" color="GREY_DARKER">
                <BreakdownValue
                  isLoading={isLoading}
                  value={financeAmount}
                  format={formatCurrencyInteger}
                />
              </Text>
            </Styled.BreakdownItem>
            <Styled.BreakdownItem>
              <Text size="M16" color="GREY_DARK">
                Typical APR
              </Text>
              <Text size="M16" weight="bold" color="GREY_DARKER">
                <BreakdownValue
                  isLoading={isLoading}
                  value={apr}
                  format={formatPercent}
                  skeletonWidth="48px"
                />
              </Text>
            </Styled.BreakdownItem>
            <Styled.BreakdownItem>
              <Text size="M16" color="GREY_DARK">
                Total cost of credit
              </Text>
              <Text size="M16" weight="bold" color="GREY_DARKER">
                <BreakdownValue
                  isLoading={isLoading}
                  value={totalCostOfCredit}
                  format={formatCurrencyInteger}
                  skeletonWidth="48px"
                />
              </Text>
            </Styled.BreakdownItem>
          </Styled.BreakdownList>
          <>
            <br />
            <LinkButton
              onClick={() => {
                trackCalculatorInteraction();
                setIsDisclaimerOpen((prev) => !prev);
                onViewFullSummary?.();
              }}
              colorVariant="GREY_DARKER"
              underline
              disabled={!loanData}
              fontSize="M16"
              fontWeight="semibold"
            >
              {isDisclaimerOpen ? 'View less' : 'View full summary'}
            </LinkButton>
            {isDisclaimerOpen && loanData?.disclaimer && (
              <>
                <Spacer marginBottom="S8" />
                <Text size="S12" color="GREY_DARK">
                  {loanData.disclaimer}
                </Text>
              </>
            )}
            {isDisclaimerOpen && loanData?.warningEarlyRepayment && (
              <>
                <Spacer marginBottom="S8" />
                <Text size="S12" weight="bold" color="GREY_DARK">
                  {loanData.warningEarlyRepayment}
                </Text>
              </>
            )}
            {isDisclaimerOpen && loanData?.warningMissedRepayments && (
              <>
                <Spacer marginBottom="S8" />
                <Text size="S12" weight="bold" color="GREY_DARK">
                  {loanData.warningMissedRepayments}
                </Text>
              </>
            )}
          </>
        </Styled.BreakdownPanel>
        <DynamicHirePurchaseModal
          isOpen={isHirePurchaseModalOpen}
          onRequestClose={() => setIsHirePurchaseModalOpen(false)}
        />
        <DynamicCashDepositModal
          isOpen={isCashDepositModalOpen}
          onRequestClose={() => setIsCashDepositModalOpen(false)}
        />
        <DynamicTradeInValueModal
          isOpen={isTradeInValueModalOpen}
          onRequestClose={() => setIsTradeInValueModalOpen(false)}
        />
        <DynamicTotalDepositModal
          isOpen={isTotalDepositModalOpen}
          onRequestClose={() => setIsTotalDepositModalOpen(false)}
        />
        <DynamicOutstandingFinanceModal
          isOpen={isOutstandingFinanceModalOpen}
          onRequestClose={() => setIsOutstandingFinanceModalOpen(false)}
        />
      </Styled.Grid>
      <Spacer marginBottom={['M16', 'L40']} />
      <Divider />
    </>
  );
};

export { FinanceCalculator };
